import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface Customer {
  id: number;
  name: string;
}

interface Batch {
  id: string;
  batch_number: string;
  customer_batch_id: string;
  profile_name: string;
  customer_name: string;
  quantity_started: number;
  arrival_date: string;
  status: string;
  status_display: string;
  current_week: number;
  expected_harvest_date: string;
  feed_line: string;
  feed_line_display: string;
  packaging_type: string;
  packaging_type_display: string;
}

interface Profile {
  id: number;
  display_name: string;
  category: string;
}

const BatchesAdmin: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [profileFilter, setProfileFilter] = useState('all');

  useEffect(() => {
    fetchBatches();
    fetchCustomers();
    fetchProfiles();
  }, [statusFilter, customerFilter, profileFilter]);

  const fetchBatches = async () => {
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer = customerFilter;
      if (profileFilter !== 'all') params.profile = profileFilter;

      const response = await api.get('/planner/admin/batches/', { params });
      setBatches(response.data);
    } catch (error) {
      console.error('Failed to load batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/planner/admin/feed-profiles/');
      setProfiles(response.data);
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }
  };

  const handleStartBatch = async (batchId: string) => {
    try {
      await api.post(`/planner/admin/batches/${batchId}/start/`);
      fetchBatches();
    } catch (error) {
      console.error('Failed to start batch:', error);
      alert('Failed to start batch');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading batches...</div>;
  }

  return (
    <div className="batches-admin-page">
      <div className="page-header">
        <h1>All Batches</h1>
        <div>
          <Link to="/planner" className="btn-secondary" style={{ marginRight: '12px' }}>
            ← Back
          </Link>
          <Link to="/planner/batches/new" className="btn-primary">
            + Add Batch
          </Link>
        </div>
      </div>

      <div className="filters-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="harvested">Harvested</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
          <option value="all">All Customers</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)}>
          <option value="all">All Animal Types</option>
          {profiles.map(p => (
            <option key={p.id} value={p.id}>{p.display_name}</option>
          ))}
        </select>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Batch #</th>
              <th>Customer</th>
              <th>Animal Type</th>
              <th>Qty</th>
              <th>Arrival</th>
              <th>Harvest</th>
              <th>Week</th>
              <th>Feed Line</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(batch => (
              <tr key={batch.id}>
                <td>
                  <Link to={`/planner/batches/${batch.id}`} className="link-primary">
                    {batch.batch_number}
                  </Link>
                  {batch.customer_batch_id && (
                    <div className="sublabel">{batch.customer_batch_id}</div>
                  )}
                </td>
                <td>{batch.customer_name}</td>
                <td>{batch.profile_name}</td>
                <td>{batch.quantity_started.toLocaleString()}</td>
                <td>{formatDate(batch.arrival_date)}</td>
                <td>{formatDate(batch.expected_harvest_date)}</td>
                <td>{batch.status === 'active' ? `Week ${batch.current_week}` : '-'}</td>
                <td>
                  <span className={`feed-line-badge ${batch.feed_line}`}>
                    {batch.feed_line_display}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${batch.status}`}>
                    {batch.status_display}
                  </span>
                </td>
                <td className="actions-cell">
                  <Link to={`/planner/batches/${batch.id}`} className="btn-small view">
                    View
                  </Link>
                  {batch.status === 'planned' && (
                    <button
                      className="btn-small edit"
                      onClick={() => handleStartBatch(batch.id)}
                    >
                      Start
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {batches.length === 0 && (
          <p className="empty-table-message">
            No batches found matching your filters
          </p>
        )}
      </div>
    </div>
  );
};

export default BatchesAdmin;
